import { useSyncExternalStore, useEffect, useCallback } from "react";
import { getContracts, getProvider, getTierFromBurned } from "@/lib/contracts";
import { createKeyedStore } from "@/lib/externalStore";

export interface CatDetails {
  tokenId: number;
  owner: string;
  tokenURI: string;
  rarityWeight: bigint;
  tier: number;
  active: boolean;
  weight: bigint;
}

interface Snapshot {
  details: CatDetails | null;
  isLoading: boolean;
}

/**
 * 猫详情采用「按 tokenId 共享 store + 手动刷新」，不做定时轮询。
 *
 * CatPreviewCard / ActivatePanel / UpgradePanel / OperationSheet 最多四处
 * 同时读同一个 tokenId，各自轮询是四倍浪费。
 * 数据只在选中猫变化与交易完成后拉取。
 */
const getStore = createKeyedStore<Snapshot>({ details: null, isLoading: false });

/** tokenURI 与 rarityWeight 在 mint 时写死，之后永不改变 */
const immutableCache = new Map<number, { tokenURI: string; rarityWeight: bigint }>();

const loaded = new Set<number>();
const inflight = new Map<number, Promise<void>>();

/** 主动刷新指定猫的详情，所有订阅该 tokenId 的组件同步更新 */
export async function refreshCatDetails(tokenId: number): Promise<void> {
  const existing = inflight.get(tokenId);
  if (existing) return existing;

  const store = getStore(String(tokenId));

  const task = (async () => {
    try {
      const { nft } = getContracts(getProvider());
      const cached = immutableCache.get(tokenId);

      // 只有未缓存过的 token 才读 tokenURI / rarityWeight
      const [owner, tierBurned, active, weight, fetched] = await Promise.all([
        nft.ownerOf(tokenId),
        nft.tierBurned(tokenId),
        nft.isActive(tokenId),
        nft.weightOf(tokenId),
        cached
          ? Promise.resolve(cached)
          : Promise.all([nft.tokenURI(tokenId), nft.rarityWeight(tokenId)]).then(
              ([tokenURI, rarityWeight]) => {
                const value = { tokenURI: tokenURI as string, rarityWeight: rarityWeight as bigint };
                immutableCache.set(tokenId, value);
                return value;
              }
            ),
      ]);

      loaded.add(tokenId);
      store.set({
        isLoading: false,
        details: {
          tokenId,
          owner: owner as string,
          tokenURI: fetched.tokenURI,
          rarityWeight: fetched.rarityWeight,
          tier: getTierFromBurned(tierBurned as bigint),
          active: active as boolean,
          weight: weight as bigint,
        },
      });
    } catch (err) {
      console.error(`[useCatDetails] 读取猫 #${tokenId} 详情失败`, err);
      store.set({ details: null, isLoading: false });
    } finally {
      inflight.delete(tokenId);
    }
  })();

  inflight.set(tokenId, task);
  return task;
}

const EMPTY: Snapshot = { details: null, isLoading: false };

export function useCatDetails(tokenId?: number | null) {
  const key = tokenId ? String(tokenId) : null;
  const store = key ? getStore(key) : null;

  const subscribe = useCallback(
    (l: () => void) => (store ? store.subscribe(l) : () => {}),
    [store]
  );
  const getSnapshot = useCallback(() => (store ? store.get() : EMPTY), [store]);

  const state = useSyncExternalStore(subscribe, getSnapshot);

  // 仅在切换猫时拉取一次；后续由交易完成或用户刷新页面触发
  useEffect(() => {
    if (tokenId && !loaded.has(tokenId) && !inflight.has(tokenId)) {
      refreshCatDetails(tokenId);
    }
  }, [tokenId]);

  const refresh = useCallback(async () => {
    if (tokenId) await refreshCatDetails(tokenId);
  }, [tokenId]);

  return { details: state.details, isLoading: state.isLoading, refresh };
}
